import React, { useState, useEffect } from "react";
import { Container, Row, Col, Button } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { FadeLoader } from "react-spinners";
import GeneralApiService from "../../../services/generalServices";
import DynamicFormikForm from "../../stages/DynamicFormikForm";
import { setShowMessage } from "../../../redux/slices/notificationSlice";

const transformData = (fields) => {
  const groupedData = fields.reduce((acc, field) => {
    const { main_field_name, ...fieldData } = field;

    if (!acc[main_field_name]) {
      acc[main_field_name] = {
        main_field_name,
        fields: [],
      };
    }

    acc[main_field_name].fields.push(fieldData);
    return acc;
  }, {});

  return Object.values(groupedData);
};

const EditSalesDataForm = () => {
  const location = useLocation();
  const { ev_stage, editData } = location?.state || {};
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const generalServices = new GeneralApiService();

  const [fields, setFields] = useState([]);
  const [optionsData, setOptionsData] = useState({});
  const [loading, setLoading] = useState(false);

  const getFields = async (category) => {
    if (!category) return;

    setLoading(true);
    try {
      const response = await generalServices.getCategoryFields(category);

      if (response?.isSuccess && response.result?.[category]) {
        const transformedData = transformData(response.result[category]);
        setFields(transformedData);
        const isSsp = String(ev_stage?.type || "").toUpperCase() === "SSP";

        if (isSsp && editData) {
          let customFieldsStr = editData.custom_fields;
          if (customFieldsStr && typeof customFieldsStr === "object") {
            customFieldsStr = JSON.stringify(customFieldsStr);
          }
          if (!customFieldsStr) {
            customFieldsStr = JSON.stringify({
              enquiry_detail: {
                primary_source:
                  editData.enquiry_detail?.primary_source ||
                  editData.primary_source_id ||
                  "",
                enquiry_source:
                  editData.enquiry_detail?.enquiry_source ||
                  editData.enquiry_source_id ||
                  "",
              },
              support_info: {
                farm_name: editData.farm_name || "",
                remarks: editData.remarks || "",
                wark: editData.wark || "",
              },
              old_product: {
                is_old_owned: editData.is_old_owned || false,
                old_make: editData.old_make || "",
                old_model: editData.old_model || "",
                old_year: editData.old_year || "",
                old_price: editData.old_price || "",
              },
            });
          }
          setOptionsData({
            ...response.data,
            categoryId: category,
            editData: {
              ...editData,
              custom_fields: customFieldsStr,
              enquiry_date:
                editData.enquiry_date || editData.created_at || editData.date,
            },
            customerData: {
              district: editData.district_id ?? editData.district,
              taluka: editData.taluka_id ?? editData.taluka,
              village: editData.village_id ?? editData.village,
            },
            sspEditMode: true,
          });
        } else {
          const updatedData = {
            ...response.data,
            categoryId: category,
          };
          if (editData?.id) {
            await generalServices
              .getCustomerDetail(editData.id)
              .then((res) => {
                if (res.isSuccess) {
                  const [rowData] = res.result.customerData;
                  updatedData["customerData"] = rowData;
                  updatedData["editData"] = editData;
                }
              });
          }
          setOptionsData(updatedData);
        }
      } else {
        console.error("Invalid response or no fields found");
        setFields([]);
      }
    } catch (error) {
      console.error("Error retrieving fields:", error);
      setFields([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getFields(ev_stage?.value);
  }, [ev_stage?.value]);

  const formatType = (type) =>
    type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();

  const handleFormSubmit = async (values) => {
    try {
      const isSsp = String(ev_stage?.type || "").toUpperCase() === "SSP";
      if (isSsp) {
        values["sspId"] = editData.ssp_id ?? editData.id;
        values["categoryId"] = ev_stage.value;
        values["categoryName"] = "ssp";
      } else {
        values["customerId"] = editData.id;
        values["enquiryId"] = editData.enquiry_id;
      }
      await generalServices.updateMultiFormData(values).then((response) => {
        if (response && response.isSuccess) {
          setFields([]);
          const formattedType = formatType(ev_stage.type);
          navigate(`/sale/data/${formattedType}`, {
            state: { stage: ev_stage },
          });
          dispatch(setShowMessage(response.message));
        }
      });
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  return (
    <Container className="bg-white rounded">
      <Row className="mb-1">
        <Col md={12}>
          <div className="d-flex align-items-center justify-content-between">
            <label className="m-0 form-label">
              EDIT{" "}
              {ev_stage?.type?.charAt(0).toUpperCase() +
                ev_stage?.type?.slice(1)}
            </label>
            <Button
              variant="outline-secondary"
              className="text-muted mt-1"
              size="small"
              onClick={() => navigate(-1)}
            >
              BACK
            </Button>
          </div>
        </Col>
      </Row>
      <Row className="justify-content-md-center">
        <Col md={12}>
          {loading ? (
            <div className="d-flex flex-column align-items-center text-center">
              <FadeLoader size={10} color="#808b96" />
              <span style={{ fontSize: "16px", color: "#808b96" }}>
                Preparing, please wait...
              </span>
            </div>
          ) : fields.length > 0 ? (
            <DynamicFormikForm
              key={ev_stage?.value}
              fields={fields}
              optionsData={optionsData}
              submitLabel="Submit"
              onSubmit={handleFormSubmit}
            />
          ) : (
            <div className="notification-container d-flex flex-column align-items-center text-center">
              <div className="icon-wrapper">
                <i
                  className="fa fa-file-text-o"
                  style={{ fontSize: "120px", color: "#007aff" }}
                ></i>
              </div>
              <p style={{ fontSize: "16px", color: "#007aff" }}>
                No fields present..
              </p>
            </div>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default EditSalesDataForm;
